import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';
import { buildLocallyToggledEntity, extractEntityFromTogglePayload } from '../../utils/toggleEntityState';

const ADMIN = '/admin';

export const fetchPackages = createAsyncThunk(
  'package/fetchPackages',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-all-packages`);
      return data?.data ?? data ?? [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch packages');
    }
  }
);

export const fetchPackageById = createAsyncThunk(
  'package/fetchPackageById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-package/${id}`);
      return data?.data ?? data ?? null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch package details');
    }
  }
);

export const fetchPackagesBySalon = createAsyncThunk(
  'package/fetchPackagesBySalon',
  async (salonId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-packages-by-salon/${salonId}`);
      return data?.data ?? data ?? [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch salon packages');
    }
  }
);

export const createPackage = createAsyncThunk(
  'package/createPackage',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${ADMIN}/create-package`, payload);
      return data?.data ?? data ?? {};
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to create package');
    }
  }
);

export const updatePackage = createAsyncThunk(
  'package/updatePackage',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`${ADMIN}/update-package/${id}`, payload);
      return data?.data ?? data ?? {};
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update package');
    }
  }
);

export const deletePackage = createAsyncThunk(
  'package/deletePackage',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${ADMIN}/delete-package/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to delete package');
    }
  }
);

export const togglePackageActive = createAsyncThunk(
  'package/togglePackageActive',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`${ADMIN}/toggle-package-active/${id}`);
      return data?.data ?? data ?? {};
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to toggle package status');
    }
  }
);

const initialState = {
  packages: [],
  selectedPackage: null,
  loading: false,
  error: null,
};

const packageSlice = createSlice({
  name: 'package',
  initialState,
  reducers: {
    clearPackageError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.loading = false;
        state.packages = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.loading = false;
        state.packages = [];
        state.error = action.payload || 'Failed to fetch packages';
      })
      .addCase(fetchPackageById.fulfilled, (state, action) => {
        state.selectedPackage = action.payload;
      })
      .addCase(fetchPackagesBySalon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPackagesBySalon.fulfilled, (state, action) => {
        state.loading = false;
        state.packages = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPackagesBySalon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch salon packages';
      })
      .addCase(createPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPackage.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.packages.unshift(action.payload);
      })
      .addCase(createPackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create package';
      })
      .addCase(updatePackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePackage.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        if (!updated) return;
        const idx = state.packages.findIndex((p) => p.id === updated.id);
        if (idx !== -1) state.packages[idx] = updated;
        if (state.selectedPackage?.id === updated.id) state.selectedPackage = updated;
      })
      .addCase(updatePackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update package';
      })
      .addCase(deletePackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePackage.fulfilled, (state, action) => {
        state.loading = false;
        state.packages = state.packages.filter((p) => p.id !== action.payload);
        if (state.selectedPackage?.id === action.payload) state.selectedPackage = null;
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete package';
      })
      .addCase(togglePackageActive.fulfilled, (state, action) => {
        const updated = extractEntityFromTogglePayload(action.payload, ['package']);
        const targetId = updated?.id ?? action.meta.arg;
        const idx = state.packages.findIndex((p) => p.id === targetId);

        if (idx !== -1) {
          state.packages[idx] = updated || buildLocallyToggledEntity(state.packages[idx]);
        }

        if (state.selectedPackage?.id === targetId) {
          state.selectedPackage = updated || buildLocallyToggledEntity(state.selectedPackage);
        }
      })
      .addCase(togglePackageActive.rejected, (state, action) => {
        state.error = action.payload || 'Failed to toggle package status';
      });
  },
});

export const { clearPackageError } = packageSlice.actions;
export default packageSlice.reducer;

export const selectPackages = (state) => state.package.packages;
export const selectPackageLoading = (state) => state.package.loading;
export const selectPackageError = (state) => state.package.error;
export const selectSelectedPackage = (state) => state.package.selectedPackage;

export function usePackage() {
  const dispatch = useDispatch();
  const packages = useSelector(selectPackages);
  const loading = useSelector(selectPackageLoading);
  const error = useSelector(selectPackageError);

  return {
    packages,
    loading,
    error,
    fetchPackages: useCallback(() => dispatch(fetchPackages()), [dispatch]),
    fetchPackageById: useCallback((id) => dispatch(fetchPackageById(id)), [dispatch]),
    fetchPackagesBySalon: useCallback((salonId) => dispatch(fetchPackagesBySalon(salonId)), [dispatch]),
    createPackage: useCallback((payload) => dispatch(createPackage(payload)), [dispatch]),
    updatePackage: useCallback(({ id, payload }) => dispatch(updatePackage({ id, payload })), [dispatch]),
    deletePackage: useCallback((id) => dispatch(deletePackage(id)), [dispatch]),
    togglePackageActive: useCallback((id) => dispatch(togglePackageActive(id)), [dispatch]),
  };
}
